'use client';

import { memo, type ReactNode, useEffect, useState } from 'react';

import { useWorkspace, useWorkspaces } from '@air/plugin-ui';
import { Spinner } from '@air/primitive-spinner';

import { LoadingState } from '~/app/(authorized)/_components/LoadingState';
import { UpgradePrompt } from '~/app/(authorized)/_components/UpgradePrompt';
import { UploadStatusBanner } from '~/app/(authorized)/_components/UploadStatusBanner';
import { ENTERPRISE_PLANS } from '~/constants/ShopifyDeliverTypes';
import { useTrackIntegrationPluginOpened } from '~/hooks/events/useTrackIntegrationPluginOpened';
import { useNavigationHistory } from '~/hooks/navigation/useNavigationHistory';
import { useURLWorkspaceId } from '~/hooks/url/useURLWorkspaceId';
import { useConfigureThirdPartyServices } from '~/hooks/useConfigureThirdPartyServices';
import { useFeatureFlags } from '~/hooks/useFeatureFlags';
import { useDeliveryStore } from '~/stores/deliveryStore';

export type AuthorizedWorkspaceLayoutProps = {
  children: ReactNode;
  header: ReactNode;
  sidebar: ReactNode;
};

const AuthorizedWorkspaceLayout = memo(({ children, header, sidebar }: AuthorizedWorkspaceLayoutProps) => {
  const [isMounted, setIsMounted] = useState(false);
  useEffect(() => setIsMounted(true), []);

  const { deliveryState, setDeliveryState } = useDeliveryStore();
  const { trackIntegrationPluginOpened } = useTrackIntegrationPluginOpened();

  useNavigationHistory();
  const { hasLaunchDarklyIdentified, isLaunchDarklyReady } = useConfigureThirdPartyServices();

  useEffect(() => {
    trackIntegrationPluginOpened();
  }, [trackIntegrationPluginOpened]);

  const { shopifyPluginEnabled } = useFeatureFlags();
  const { isLoading: isLoadingWorkspaces } = useWorkspaces();
  const selectedWorkspaceId = useURLWorkspaceId();
  const { data: workspace } = useWorkspace({ workspaceId: selectedWorkspaceId ?? undefined });

  const canSkipLoading = isMounted && !!selectedWorkspaceId;
  // `canSkipLoading` waives the workspaces *list* only; the access gate below needs this
  // workspace's plan, so a missing `workspace` must hold loading rather than paywall on no data.
  const shouldShowLoading = !isMounted || (!canSkipLoading && isLoadingWorkspaces) || !workspace;

  if (shouldShowLoading) {
    return <LoadingState />;
  }

  const isEnterprisePlan = ENTERPRISE_PLANS.has(workspace.plan);
  // Non-enterprise workspaces can be granted access by the flag, but only once LD has identified them.
  const hasPluginAccess = isEnterprisePlan || (hasLaunchDarklyIdentified && shopifyPluginEnabled);
  // LaunchDarkly reports every flag as false until ready, which would flash the paywall at
  // customers who do have access. Enterprise never consults the flag, so only the rest wait.
  const isPluginAccessPending = !isEnterprisePlan && !isLaunchDarklyReady;

  let content: ReactNode = children;

  if (isPluginAccessPending) {
    content = (
      <div className="flex flex-1 items-center justify-center">
        <Spinner className="size-8 text-altitude-9" />
      </div>
    );
  } else if (!hasPluginAccess) {
    content = <UpgradePrompt />;
  }

  return (
    <div className="flex h-full flex-col">
      <UploadStatusBanner deliveryState={deliveryState} onDismiss={() => setDeliveryState({ status: 'idle' })} />
      <main className="relative flex min-h-0 flex-1 bg-white dark:bg-gray-1">
        {sidebar}
        <div className="flex flex-1 flex-col overflow-hidden">
          {header}
          <div className="relative flex min-h-0 flex-1 flex-col">{content}</div>
        </div>
      </main>
    </div>
  );
});

AuthorizedWorkspaceLayout.displayName = 'AuthorizedWorkspaceLayout';

export default AuthorizedWorkspaceLayout;
